// Client for the port pages: static per-port metadata (built by prerender.ports.mjs), the public
// live congestion signal, and a storm-threat check derived from the live cyclones feed.

import { KEYED_BASE } from './publicApi'
import { type Cyclone, fetchCyclones } from './weather-api'

const API_BASE = (import.meta.env.VITE_API_BASE as string) ?? 'https://schedulesmcp-api.fly.dev/v1'
const PUBLIC_BASE = `${API_BASE.replace(/\/v1\/?$/, '')}/public`

export interface PortLane {
  slug: string
  role: 'origin' | 'dest'
  otherCode: string
  otherName: string
  trade: string
  carriers: number
  sailings: number
}

/** Evergreen authority facts + rankings baked into the per-port JSON by the build. */
export interface PortFacts {
  operator?: string | null
  port_authority?: string | null
  teu_annual?: number | null
  teu_year?: number | null
  max_draft_m?: number | null
  berths?: number | null
  quay_length_m?: number | null
  website?: string | null
  wikidata_id?: string | null
  terminals?: string[] | null
  cppi_rank?: number | null
  cppi_year?: number | null
  plsci?: number | null
  plsci_quarter?: string | null
}

export interface PortMeta {
  code: string
  name: string
  cc: string
  region: string
  lat: number
  lng: number
  sailings: number
  laneCount: number
  carriers: number
  lanes: PortLane[]
  facts?: PortFacts | null
}

export interface PortCongestion {
  port: string
  congested: boolean
  level: 'mild' | 'moderate' | 'severe' | null
  expected_wait_days: number | null
  confidence: number | null
  measured: boolean
  narrative: string | null
}

/** Static metadata for a port, written per-port by the build. */
export async function fetchPortMeta(code: string): Promise<PortMeta | null> {
  try {
    const res = await fetch(`/data/ports/${code.toUpperCase()}.json`)
    if (!res.ok) return null
    return (await res.json()) as PortMeta
  } catch {
    return null
  }
}

/** Live fused congestion signal. Keyed since 07.10, so read through the site's key (site-api.js). */
export async function fetchPortCongestion(code: string): Promise<PortCongestion | null> {
  try {
    const res = await fetch(`${KEYED_BASE}/port-congestion?port=${encodeURIComponent(code)}`)
    if (!res.ok) return null
    const json = (await res.json()) as { data?: PortCongestion }
    return json.data ?? null
  } catch {
    return null
  }
}

/** Live shop-window read for the port page (tracked vessels at port, congestion rank).
 *  vesselsAtPort = vessels in OUR AIS coverage currently at the port (arrived, not departed) —
 *  a tracked subset, not the port's full vessel population. */
export interface PortLive {
  vesselsAtPort: number
  /** The congestion level, the only congestion fact the open page carries (owner 07.10); null = clear. */
  congestionLevel: PortCongestion['level']
}

const LEVELS = new Set(['mild', 'moderate', 'severe'])

/** Live port payload from GET /public/port/:code: vessels at port and the congestion level. */
export async function fetchPortLive(code: string): Promise<PortLive | null> {
  try {
    const res = await fetch(`${PUBLIC_BASE}/port/${encodeURIComponent(code.toUpperCase())}`)
    if (!res.ok) return null
    const json = (await res.json()) as {
      data?: {
        vessels_at_port?: unknown[]
        congestion?: { level?: string | null } | null
      }
    }
    const d = json.data
    if (!d) return null
    const level = (d.congestion?.level ?? '').toLowerCase()
    return {
      vesselsAtPort: Array.isArray(d.vessels_at_port) ? d.vessels_at_port.length : 0,
      congestionLevel: LEVELS.has(level) ? (level as PortCongestion['level']) : null,
    }
  } catch {
    return null
  }
}

/** One daily point of the 90-day congestion trend (score = AIS density_pct). */
export interface PortHistoryPoint {
  date: string
  score: number | null
  level: string | null
}

/** 90-day congestion history from GET /public/port/:code/history (oldest→newest; [] if none). */
export async function fetchPortHistory(code: string): Promise<PortHistoryPoint[]> {
  try {
    const res = await fetch(`${PUBLIC_BASE}/port/${encodeURIComponent(code.toUpperCase())}/history`)
    if (!res.ok) return []
    const json = (await res.json()) as { data?: { points?: PortHistoryPoint[] } }
    return json.data?.points ?? []
  } catch {
    return []
  }
}

/** Compact live marine/weather conditions at the port (Open-Meteo, keyless). */
export interface PortConditions {
  windKt: number | null
  gustKt: number | null
  waveM: number | null
  fog: boolean
}

/** Current wind (kt) + wave (m) + fog flag at a port from Open-Meteo. null on any failure. */
export async function fetchPortConditions(
  lat: number,
  lng: number
): Promise<PortConditions | null> {
  try {
    const fUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=wind_speed_10m,wind_gusts_10m,visibility,weather_code&wind_speed_unit=kn`
    const mUrl = `https://marine-api.open-meteo.com/v1/marine?latitude=${lat}&longitude=${lng}&current=wave_height`
    const [fRes, mRes] = await Promise.all([fetch(fUrl), fetch(mUrl).catch(() => null)])
    if (!fRes.ok) return null
    const f = (await fRes.json()) as {
      current?: { wind_speed_10m?: number; wind_gusts_10m?: number; visibility?: number }
    }
    const cur = f.current
    if (!cur) return null
    let waveM: number | null = null
    if (mRes?.ok) {
      const m = (await mRes.json()) as { current?: { wave_height?: number } }
      waveM = m.current?.wave_height ?? null
    }
    return {
      windKt: cur.wind_speed_10m ?? null,
      gustKt: cur.wind_gusts_10m ?? null,
      waveM,
      // Open-Meteo visibility is metres; fog when sub-1 km.
      fog: cur.visibility != null && cur.visibility < 1000,
    }
  } catch {
    return null
  }
}

/** Upcoming public holiday for the port's country (Nager.Date, keyless). */
export interface PortHoliday {
  date: string
  localName: string
  name: string
}

/** Next public holidays for an ISO-2 country code. [] when the country is unknown (404) or on error. */
export async function fetchNextHolidays(cc: string): Promise<PortHoliday[]> {
  try {
    const res = await fetch(`https://date.nager.at/api/v3/NextPublicHolidays/${cc.toUpperCase()}`)
    if (!res.ok) return []
    const arr = (await res.json()) as PortHoliday[]
    return Array.isArray(arr) ? arr : []
  } catch {
    return []
  }
}

export interface StormThreat {
  cycloneId: string
  name: string
  category: string | null
  distanceKm: number
  whenIso: string | null // forecast time of closest approach (null = current position)
}

const EARTH_R_KM = 6371
function haversineKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const dLat = ((b.lat - a.lat) * Math.PI) / 180
  const dLon = ((b.lon - a.lon) * Math.PI) / 180
  const la1 = (a.lat * Math.PI) / 180
  const la2 = (b.lat * Math.PI) / 180
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_R_KM * Math.asin(Math.min(1, Math.sqrt(h)))
}

// Rough category ordering for a per-fix radius scale when a fix carries no wind speed. A weakening
// forecast (e.g. TY→TS) should shrink the gale field, not keep the current storm's radius.
const CATEGORY_SCALE: Record<string, number> = {
  TD: 0.5,
  TS: 0.7,
  STS: 0.85,
  TY: 1,
  STY: 1.15,
  SuperTY: 1.3,
}

/**
 * Gale radius for ONE track fix. The cyclone's `galeRadiusKm` is its CURRENT gale field (tied to its
 * current intensity), so applying it to every forecast point over-reaches when the storm is forecast to
 * weaken. Scale it per fix by that fix's own intensity: by wind ratio when both winds are known (the
 * gale field scales ~linearly with wind), else by category, else fall back to the current radius. (Codex
 * #106: use per-forecast radii so a shrinking storm stops firing a false exposure card.)
 */
function fixRadiusKm(c: Cyclone, fix: { windMs: number | null; category: string | null }): number {
  const base = c.galeRadiusKm ?? 120
  let scale = 1
  if (fix.windMs != null && c.windMs != null && c.windMs > 0) {
    scale = fix.windMs / c.windMs
  } else if (fix.category && CATEGORY_SCALE[fix.category] != null) {
    const cur = (c.category && CATEGORY_SCALE[c.category]) || CATEGORY_SCALE.TY || 1
    scale = (CATEGORY_SCALE[fix.category] as number) / cur
  }
  // Clamp so a noisy ratio can't explode or collapse the field.
  scale = Math.max(0.35, Math.min(1.5, scale))
  return base * scale
}

/** Does any active cyclone's gale field (current or forecast) reach this port? Closest threat, if any. */
export function portStormThreat(
  port: { lat: number; lon: number },
  cyclones: Cyclone[],
  bufferKm = 75
): StormThreat | null {
  let best: (StormThreat & { slack: number }) | null = null
  for (const c of cyclones) {
    // Candidate fixes: the current center (current intensity) + every forecast point (its own intensity).
    const fixes: Array<{
      lat: number
      lon: number
      at: string | null
      windMs: number | null
      category: string | null
    }> = [
      {
        lat: c.center.lat,
        lon: c.center.lon,
        at: c.observedAt,
        windMs: c.windMs,
        category: c.category,
      },
      ...c.track
        .filter((t) => t.kind === 'forecast')
        .map((t) => ({ lat: t.lat, lon: t.lon, at: t.at, windMs: t.windMs, category: t.category })),
    ]
    for (const f of fixes) {
      const radius = fixRadiusKm(c, f) + bufferKm
      const d = haversineKm(port, { lat: f.lat, lon: f.lon })
      if (d > radius) continue
      const slack = d - radius
      if (!best || slack < best.slack) {
        best = {
          cycloneId: c.id,
          name: c.name,
          category: c.category,
          distanceKm: Math.round(d),
          whenIso: f.at,
          slack,
        }
      }
    }
  }
  if (!best) return null
  const { slack: _slack, ...threat } = best
  return threat
}

export { fetchCyclones }
